import { apiSlice } from './apiSlice';
import { ApiResponse } from '@/types';
import { handleOptimisticDelete, handleOptimisticUpdate, handleOptimisticCreate } from './optimisticUpdates';

export type NewsStatus = 'Draft' | 'Published';

export interface News {
  id: number;
  title: string;
  description: string;
  date: string;
  status: NewsStatus;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedNewsResponse {
  count: number;
  skip: number;
  take: number;
  data: News[];
}

export interface GetNewsArgs {
  search?: string;
  skip?: number;
  take?: number;
  status?: NewsStatus;
}

export type CreateNewsPayload = Omit<News, 'id' | 'createdAt' | 'updatedAt'>;
export type UpdateNewsPayload = Partial<CreateNewsPayload>;

export const newsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNews: builder.query<PaginatedNewsResponse, GetNewsArgs | void>({
      query: (args) => {
        let url = '/news';
        const params = new URLSearchParams();
        if (args) {
          if (args.search) params.append('search', args.search);
          if (args.skip !== undefined) params.append('skip', String(args.skip));
          if (args.take !== undefined) params.append('take', String(args.take));
          if (args.status) params.append('status', args.status);
        }
        params.append('_t', Date.now().toString());
        const qs = params.toString();
        if (qs) url += `?${qs}`;
        return url;
      },
      transformResponse: (response: ApiResponse<PaginatedNewsResponse>) => response.data,
      providesTags: ['News'],
    }),

    getNewsById: builder.query<News, string>({
      query: (slug) => `/news/${slug}?_t=${Date.now()}`,
      transformResponse: (response: ApiResponse<News>) => response.data,
      providesTags: (_result, _err, slug) => [{ type: 'News', id: slug }],
    }),

    createNews: builder.mutation<News, CreateNewsPayload>({
      query: (body) => ({ url: '/news', method: 'POST', body }),
      transformResponse: (response: ApiResponse<News>) => response.data,
      invalidatesTags: ['News'],
    }),

    updateNews: builder.mutation<News, { id: number; body: UpdateNewsPayload }>({
      query: ({ id, body }) => ({ url: `/news/${id}`, method: 'PATCH', body }),
      transformResponse: (response: ApiResponse<News>) => response.data,
      invalidatesTags: ['News'],
    }),

    deleteNews: builder.mutation<void, number>({
      query: (id) => ({ url: `/news/${id}`, method: 'DELETE' }),
      invalidatesTags: ['News'],
    }),
  }),
});

export const {
  useGetNewsQuery,
  useGetNewsByIdQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
} = newsApi;
